<ng-container *transloco="let t; prefix: 'tasks.taskDetail'">
  <ng-template #priorityControlTemplate>
    @if (currentUserCanEdit) {
      <button
        type="button"
        class="priority-selector-trigger body-medium-emphasized"
        [class.priority-selector-trigger-low]="selectedPriority.value === 'low'"
        [class.priority-selector-trigger-medium]="selectedPriority.value === 'medium'"
        [class.priority-selector-trigger-high]="selectedPriority.value === 'high'"
        [matMenuTriggerFor]="priorityMenu"
        #priorityMenuTrigger="matMenuTrigger"
        [attr.aria-label]="selectedPriority.label">
        <span>{{ selectedPriority.label }}</span>
        <i
          class="fa-regular priority-selector-chevron"
          [class.fa-chevron-up]="priorityMenuTrigger.menuOpen"
          [class.fa-chevron-down]="!priorityMenuTrigger.menuOpen"
          aria-hidden="true"></i>
      </button>

      <mat-menu
        #priorityMenu="matMenu"
        class="priority-selector-menu"
        [overlapTrigger]="false">
        @for (priority of priorities; track priority.value) {
          <button
            mat-menu-item
            type="button"
            class="priority-selector-option"
            [class.priority-selector-option-selected]="priority.value === selectedPriority.value"
            (click)="selectPriority(priority)">
            <ui-priority-status [priority]="priorityStatusValue(priority.value)"></ui-priority-status>
          </button>
        }
      </mat-menu>
    } @else {
      <ui-priority-status [priority]="task?.priorita"></ui-priority-status>
    }
  </ng-template>

  <ng-template #summaryCardTemplate>
    <mat-card class="summary-card">
      @if (isDesktop) {
        <div class="summary-card-desktop-header">
          <div class="summary-card-date">
            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
            <span class="body-medium">
              @if (task?.dataTask) {
                {{ task.dataTask | translocoDate: { day: '2-digit', month: '2-digit', year: 'numeric' } }}
                {{ task.dataTask | translocoDate: { hour: '2-digit', minute: '2-digit' } }}
              } @else { - }
            </span>
          </div>

          <div class="summary-card-priority">
            <span class="body-medium">{{ t('summaryCard.priority') }}</span>
            <ng-container *ngTemplateOutlet="priorityControlTemplate"></ng-container>
          </div>
        </div>

        <div class="summary-card-divider" aria-hidden="true"></div>

        <div class="summary-card-desktop-details">
          <div class="summary-card-detail">
            <span class="body-medium">{{ t('summaryCard.companyName') }}</span>
            <span class="body-medium-emphasized">{{ task?.titolo || '-' }}</span>
          </div>

          <div class="summary-card-detail">
            <span class="body-medium">{{ t('summaryCard.sentBy') }}</span>
            <span class="body-medium-emphasized">{{ task?.dettaglio?.requestedBy || '-' }}</span>
          </div>

          <div class="summary-card-detail summary-card-amount">
            <span class="body-medium">{{ t('summaryCard.amount') }}</span>
            <span class="headline-small-emphasized">
              @if (task?.importo !== null && task?.importo !== undefined) {
                {{ task.importo | translocoCurrency: 'symbol' : {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                } : 'EUR' }}
              } @else { - }
            </span>
          </div>
        </div>
      } @else {
        <div class="summary-row">
          <span class="body-small">
            @if (task?.dataTask) {
              {{ task.dataTask | translocoDate: { day: '2-digit', month: '2-digit', year: 'numeric' } }}
              {{ task.dataTask | translocoDate: { hour: '2-digit', minute: '2-digit' } }}
            } @else { - }
          </span>
          <ng-container *ngTemplateOutlet="priorityControlTemplate"></ng-container>
        </div>

        <div class="summary-row">
          <span class="body-small">{{ t('summaryCard.companyName') }}</span>
          <span class="summary-value body-medium-emphasized">{{ task?.titolo || '-' }}</span>
        </div>

        <div class="summary-row">
          <span class="body-small">{{ t('summaryCard.sentBy') }}</span>
          <span class="summary-value body-medium-emphasized">{{ task?.dettaglio?.requestedBy || '-' }}</span>
        </div>

        <div class="summary-row">
          <span class="body-small">{{ t('summaryCard.amount') }}</span>
          <span class="summary-value headline-small-emphasized">
            @if (task?.importo !== null && task?.importo !== undefined) {
              {{ task.importo | translocoCurrency: 'symbol' : {
                useGrouping: true,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              } : 'EUR' }}
            } @else { - }
          </span>
        </div>
      }
    </mat-card>
  </ng-template>

  <ng-template #approvalInputTemplate>
    <div class="approval-input-block">
      <label for="task-comment" class="title-small-emphasized">
        {{ t('approveTask.commentRequired') }}
      </label>

      <textarea
        id="task-comment"
        class="approval-comment-textarea body-medium"
        [(ngModel)]="comment"
        [placeholder]="t('approveTask.comment')"
        [disabled]="!currentUserCanEdit"
        required></textarea>

      <div class="approval-attachment-block">
        <div class="approval-attachment-summary">
          <span class="title-small-emphasized">
            {{ t('approveTask.attachments') }}
            @if (files.length > 0) {
              [{{ files.length }}]
            }
          </span>

          @if (files.length > 0) {
            <div class="approval-attachment-list">
              @for (file of files; track file; let fileIndex = $index) {
                <div class="approval-attachment-item">
                  <span class="body-small">{{ file.name }}</span>
                  <button
                    matIconButton
                    type="button"
                    class="approval-attachment-delete"
                    [disabled]="!currentUserCanEdit"
                    [attr.aria-label]="t('approveTask.removeAttachment')"
                    (click)="removeFile(fileIndex)">
                    <i class="fa-regular fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              }
            </div>
          } @else {
            <span class="body-small">{{ t('approveTask.noAttachments') }}</span>
          }
        </div>

        <button
          matButton="outlined"
          type="button"
          class="approval-attachment-button"
          [disabled]="!currentUserCanEdit"
          (click)="openAttachmentDialog()">
          <i class="fa-regular fa-paperclip" aria-hidden="true"></i>
          <span>{{ t('approveTask.attach') }}</span>
        </button>

      </div>
    </div>
  </ng-template>

  <ng-template #taskActionsTemplate let-desktop="desktop">
    <div
      class="task-detail-actions"
      [class.task-detail-actions-desktop]="desktop">
      <button
        matButton="outlined"
        type="button"
        class="task-detail-action-button task-detail-reject-button"
        [disabled]="!currentUserCanEdit || !comment.trim()"
        (click)="onTaskApprovalManagement(false)">
        {{ t('approveTask.rejectBtn') }}
      </button>

      <button
        matButton="filled"
        type="button"
        class="task-detail-action-button"
        [disabled]="!currentUserCanEdit || !comment.trim()"
        (click)="onTaskApprovalManagement(true)">
        {{ t('approveTask.approveBtn') }}
      </button>
    </div>
  </ng-template>

  <ng-template #anomaliesCardTemplate>
    <div
      class="summary-card summary-card-secondary collapsible-card description-card"
      [attr.role]="isDesktop ? null : 'button'"
      [attr.tabindex]="isDesktop ? null : 0"
      [attr.aria-expanded]="isDesktop ? null : descriptionCardExpanded"
      (click)="toggleDescriptionCard()"
      (keydown)="onDescriptionCardKeydown($event)">
      <div class="collapsible-card-header">
        <span class="title-medium-emphasized">{{ t('summaryCard.description', { count: anomalies.length }) }}</span>

        @if (!isDesktop) {
          <i
            class="fa-regular collapsible-card-chevron"
            [class.fa-chevron-up]="descriptionCardExpanded"
            [class.fa-chevron-down]="!descriptionCardExpanded"
            aria-hidden="true"></i>
        }
      </div>

      @if (isDesktop || descriptionCardExpanded) {
        <div class="description-card-content">
          @for (anomaly of anomalies; track $index) {
            <div class="description-card-row">
              <i class="fa-regular fa-triangle-exclamation description-alert-icon" aria-hidden="true"></i>
              <span class="description-card-text body-medium">{{ anomaly }}</span>
            </div>
          }
        </div>
      }
    </div>
  </ng-template>

  <ng-template #unitMarginTemplate let-text>
    @let unitMargin = toNumber(text);
    <span class="title-medium-emphasized" [class.property-value-error]="(unitMargin ?? 0) < 0">
      @if (unitMargin != null) {
        {{ unitMargin | translocoCurrency: 'symbol' : {
          useGrouping: true,
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        } : 'EUR' }}
      } @else {
        -
      }
    </span>
  </ng-template>

  <ng-template #changeValueTemplate let-value let-change="change">
    @if (value == null) {
      -
    } @else {
      @switch (change.campo) {
        @case ('Quantita') {
          {{ value | translocoDecimal: { useGrouping: true, maximumFractionDigits: 0 } }} {{ t('products.piecesUnit') }}
        }
        @case ('Prezzo') {
          {{ value | translocoCurrency: 'symbol' : {
            useGrouping: true,
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
          } : 'EUR' }}@if (change.unitaMisura) {/{{ change.unitaMisura }}}
        }
        @case ('PercProvvigione') {
          {{ (value / 100) | translocoPercent: { minimumFractionDigits: 2, maximumFractionDigits: 2 } }}
        }
        @default {
          {{ (value / 100) | translocoPercent: { minimumFractionDigits: 0, maximumFractionDigits: 0 } }}
        }
      }
    }
  </ng-template>

  <mat-tab-group
    class="task-detail-tabs"
    animationDuration="0ms"
    mat-align-tabs="start"
    mat-stretch-tabs="true">
    <mat-tab [label]="t('tabs.summary')">
      <div class="tab-content">
        @if (isDesktop) {
          <div class="summary-card-full-width">
            <ng-container *ngTemplateOutlet="summaryCardTemplate"></ng-container>
          </div>

          <div class="summary-side-column">
            @if (anomalies.length) {
              <ng-container *ngTemplateOutlet="anomaliesCardTemplate"></ng-container>
            }
            <ng-container *ngTemplateOutlet="approvalInputTemplate"></ng-container>
          </div>
        } @else {
          <ng-container *ngTemplateOutlet="summaryCardTemplate"></ng-container>
        }

        @if (!isDesktop && anomalies.length) {
          <ng-container *ngTemplateOutlet="anomaliesCardTemplate"></ng-container>
        }

        <div class="summary-history-column">
          <div
            class="summary-card summary-card-secondary collapsible-card activity-history-card"
          [attr.role]="isDesktop ? null : 'button'"
          [attr.tabindex]="isDesktop ? null : 0"
          [attr.aria-expanded]="isDesktop ? null : activityHistoryExpanded"
          (click)="toggleActivityHistoryCard()"
          (keydown)="onActivityHistoryCardKeydown($event)">
          <div class="collapsible-card-header">
            <span class="title-medium-emphasized">{{ t('taskHistory', { count: history.length }) }}</span>

            @if (!isDesktop) {
              <i
                class="fa-regular collapsible-card-chevron"
                [class.fa-chevron-up]="activityHistoryExpanded"
                [class.fa-chevron-down]="!activityHistoryExpanded"
                aria-hidden="true"></i>
            }
          </div>

          @if (isDesktop || activityHistoryExpanded) {
            <div #activityHistoryScroll class="activity-history-card-content">
              @for (step of history; track $index; let isLast = $last) {
                @let future = !step.outcome && !step.current;
                @let rejected = step.outcome === 'Rejected';
                <div
                  class="activity-history-entry"
                  [class.activity-history-entry-done]="!!step.outcome && !rejected"
                  [class.activity-history-entry-rejected]="rejected"
                  [class.activity-history-entry-current]="step.current"
                  [class.activity-history-entry-future]="future"
                  [class.activity-history-entry-last]="isLast">
                  <div class="activity-history-step">
                    <span
                      class="activity-history-marker"
                      [class.activity-history-marker-current]="step.current"
                      [class.activity-history-marker-pulse]="step.current && currentUserCanEdit"
                      aria-hidden="true">
                      <i
                        class="activity-history-marker-icon"
                        [class.fa-regular]="future"
                        [class.fa-solid]="!future"
                        [class.fa-circle-check]="!!step.outcome && !rejected"
                        [class.fa-circle-xmark]="rejected"
                        [class.fa-circle]="!step.outcome"></i>
                    </span>
                    <div class="activity-history-role-content">
                      <!-- Step futuro: «Prossimo approvatore:»; gli altri la data, per quello in corso da quando e' in carico -->
                      @if (future) {
                        <span class="activity-history-role-date body-medium">{{ t('history.nextApprover') }}</span>
                      } @else if (step.date) {
                        <span class="activity-history-role-date body-medium" [class.activity-history-date-current]="step.current">
                          {{ step.date | translocoDate: { day: '2-digit', month: '2-digit', year: 'numeric' } }}
                          · {{ step.date | translocoDate: { hour: '2-digit', minute: '2-digit' } }}
                          @if (step.current) {
                            · {{ t('history.inCharge') }}
                          }
                        </span>
                      }
                      @if (step.user) {
                        <span class="activity-history-user body-medium" [class.activity-history-user-future]="future">
                          {{ step.user }}@if (step.group) { - <span class="body-medium-emphasized">{{ step.group }}</span>}
                        </span>
                      }
                      @if (step.outcome) {
                        <span class="activity-history-role-notes body-medium">
                          {{ step.textKey ? t('history.' + step.textKey) : step.text }}@if (step.role) { [{{ step.role }}]}
                        </span>
                      }
                      @for (change of step.changes; track $index) {
                        <span class="activity-history-role-notes body-medium-emphasized">
                          {{ change.nomeProdotto || '-' }} · {{ t(changeFieldKeys[change.campo]) }}:
                          <ng-container *ngTemplateOutlet="changeValueTemplate; context: { $implicit: change.prima, change: change }"></ng-container>
                          →
                          <ng-container *ngTemplateOutlet="changeValueTemplate; context: { $implicit: change.dopo, change: change }"></ng-container>
                        </span>
                      }
                      @if (step.note) {
                        <span class="activity-history-role-notes body-medium">{{ step.note }}</span>
                      }
                    </div>
                  </div>

                  @if (step.attachments.length) {
                    <div class="activity-history-attachments">
                      @for (allegato of step.attachments; track allegato.id) {
                        <div class="activity-attachment-card">
                          <i class="fa-regular fa-file-lines activity-attachment-file-icon" aria-hidden="true"></i>

                          <div class="activity-attachment-description">
                            <span class="activity-attachment-name body-medium-emphasized">
                              {{ getAllegatoFileName(allegato.filePath) }}
                            </span>
                          </div>

                          <button
                            type="button"
                            class="activity-attachment-download"
                            [attr.aria-label]="t('downloadAttachment')"
                            (click)="$event.stopPropagation(); downloadAttachment(allegato, step.taskId)"
                            (keydown)="$event.stopPropagation()">
                            <i class="fa-regular fa-download" aria-hidden="true"></i>
                          </button>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
          </div>

          @if (isDesktop) {
            <ng-container
              *ngTemplateOutlet="taskActionsTemplate; context: { desktop: true }">
            </ng-container>
          }
        </div>

        @if (!isDesktop) {
          <ng-container *ngTemplateOutlet="approvalInputTemplate"></ng-container>
        }

      </div>
    </mat-tab>
    <mat-tab [label]="t('tabs.properties')">
      <div class="properties-content">
        <section class="property-section customer-property-section">
          <span class="property-section-title title-large-emphasized">
            {{ t('taskProp.customer') }}
          </span>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.expired') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(task?.dettaglio?.customerOverdueAmount ?? 0) < 0">
              {{ (task?.dettaglio?.customerOverdueAmount | translocoCurrency: 'symbol' : { useGrouping: true }) ?? '-' }}
            </span>
          </div>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.creditLimit') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(toNumber(task?.dettaglio?.customerCreditLimitExceeded) ?? 0) < 0">
              {{ (toNumber(task?.dettaglio?.customerCreditLimitExceeded) | translocoCurrency: 'symbol' : {
                useGrouping: true,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? '-' }}
            </span>
          </div>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.creditSystem') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(task?.dettaglio?.systemGrantedCreditLimit ?? 0) < 0">
              @if (task?.dettaglio?.systemGrantedCreditLimit != null) {
                {{ task.dettaglio.systemGrantedCreditLimit | translocoCurrency: 'symbol' : { useGrouping: true } }}
              } @else {
                -
              }
            </span>
          </div>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.creditData') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(task?.dettaglio?.customerReliability ?? 0) < 0">
              @if (task?.dettaglio?.customerReliability != null) {
                {{ task.dettaglio.customerReliability | translocoCurrency: 'symbol' : {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                } }}
              } @else {
                -
              }
            </span>
          </div>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.customerStatus') }}</span>
            <span class="title-medium-emphasized">{{ task?.dettaglio?.customerStatus || '-' }}</span>
          </div>

          <div class="property-card">
            <span class="body-small">{{ t('taskProp.customerType') }}</span>
            <span class="title-medium-emphasized">{{ task?.dettaglio?.customerType ?? '-' }}</span>
          </div>

          @if (task?.taskSecurityMask?.canReadManagerUnitMarginOnOrder) {
            <div class="property-card">
              <span class="body-small">{{ t('taskProp.creditCommittee') }}</span>
              <span
                class="title-medium-emphasized"
                [class.property-value-error]="(task?.dettaglio?.committeeApprovedCreditLimit ?? 0) < 0">
                @if (task?.dettaglio?.committeeApprovedCreditLimit != null) {
                  {{ task.dettaglio.committeeApprovedCreditLimit | translocoCurrency: 'symbol' : {
                    useGrouping: true,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  } }}
                } @else {
                  -
                }
              </span>
            </div>
          }

          @if (task?.taskSecurityMask?.canReadProcuratorUnitMarginOnCustomer) {
            <div class="property-card">
              <span class="body-small">{{ t('taskProp.aumCustomer') }}</span>
              <ng-container *ngTemplateOutlet="unitMarginTemplate; context: { $implicit: task?.dettaglio?.procuratorUnitMarginOnCustomer }"></ng-container>
            </div>
          }

          @if (task?.taskSecurityMask?.canReadManagerUnitMarginOnCustomer) {
            <div class="property-card">
              <span class="body-small">{{ t('taskProp.mumCustomer') }}</span>
              <ng-container *ngTemplateOutlet="unitMarginTemplate; context: { $implicit: task?.dettaglio?.managerUnitMarginOnCustomer }"></ng-container>
            </div>
          }
        </section>

        <section class="property-section">
          <span class="property-section-title title-large-emphasized">
            {{ t('taskProp.job') }}
          </span>

          <div class="property-card property-card-wide">
            <span class="body-small">{{ t('taskProp.value') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(task?.dettaglio?.orderTotalAmount ?? 0) < 0">
              {{ (task?.dettaglio?.orderTotalAmount | translocoCurrency: 'symbol' : { useGrouping: true }) ?? '-' }}
            </span>
          </div>

          <div class="property-card property-card-wide">
            <span class="body-small">{{ t('taskProp.margin') }}</span>
            <span
              class="title-medium-emphasized"
              [class.property-value-error]="(task?.dettaglio?.orderMargin ?? 0) < 0">
              @if (task?.dettaglio?.orderMargin != null) {
                {{ task.dettaglio.orderMargin | translocoDecimal: {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                } }}%
              } @else {
                -
              }
            </span>
          </div>

          @if (task?.taskSecurityMask?.canReadManagerUnitMarginOnOrder) {
            <div class="property-card property-card-wide">
              <span class="body-small">{{ t('taskProp.managerEvMargin') }}</span>
              <span class="title-medium-emphasized">
                @if (task?.dettaglio?.managerEvaluatedMargin != null) {
                  {{ task.dettaglio.managerEvaluatedMargin | translocoDecimal: {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  } }}%
                } @else {
                  -
                }
              </span>
            </div>
          }

          @if (task?.taskSecurityMask?.canReadProcuratorUnitMarginOnOrder) {
            <div class="property-card property-card-half">
              <span class="body-small">{{ t('taskProp.aumOrder') }}</span>
              <ng-container *ngTemplateOutlet="unitMarginTemplate; context: { $implicit: task?.dettaglio?.procuratorUnitMarginOnOrder }"></ng-container>
            </div>
          }

          @if (task?.taskSecurityMask?.canReadManagerUnitMarginOnOrder) {
            <div class="property-card property-card-half">
              <span class="body-small">{{ t('taskProp.mumOrder') }}</span>
              <ng-container *ngTemplateOutlet="unitMarginTemplate; context: { $implicit: task?.dettaglio?.managerUnitMarginOnOrder }"></ng-container>
            </div>
          }
        </section>

        @if (isDesktop) {
          <ng-container
            *ngTemplateOutlet="taskActionsTemplate; context: { desktop: true }">
          </ng-container>
        }
      </div>
    </mat-tab>
    <mat-tab [label]="t('tabs.products')">
      <div
        class="products-content"
        [class.products-content-editing]="productEditMode"
        [class.products-content-has-changes]="productEditMode && hasProductChanges">
        @if (productEditMode && hasProductChanges) {
          <div class="product-changes-summary" role="status" aria-live="polite">
            <i class="fa-solid fa-circle-info product-changes-summary-icon" aria-hidden="true"></i>
            <div class="product-changes-summary-copy">
              @if (modifiedProductFieldsCount > 0) {
                <span class="body-medium-emphasized">
                  {{ t('products.editDialog.modifiedFields', { count: modifiedProductFieldsCount }) }}
                </span>
              }
              @if (removedProductItemsCount > 0) {
                <span class="body-medium-emphasized">
                  {{ t('products.editDialog.removedProducts', { count: removedProductItemsCount }) }}
                </span>
              }
            </div>
          </div>
        }

        @if (task?.currentUserCanModify && !productEditMode) {
          <button
            matButton="outlined"
            type="button"
            class="products-edit-button"
            (click)="openProductEditWarning()">
          {{ t('products.edit') }}
        </button>
        }

        <mat-card class="product-family-card">
          <span class="body-small">{{ t('products.family') }}</span>
          <span class="product-family-value title-medium-emphasized">
            {{ task?.dettaglio?.productFamily || '-' }}
          </span>
        </mat-card>

        <div class="product-items-list">
          @for (item of displayedProductItems; track item.id; let isLast = $last) {
            @let originalItem = getOriginalProductItem(item);
            <div class="product-item">
              <div class="product-item-header">
                <div class="product-item-heading">
                  <span class="title-large-emphasized">{{ item.descrizioneProdotto || '-' }}</span>
                  <span class="body-small">{{ item.sku ?? item.codiceProdotto ?? '-' }}@if (item.nomeImballo) { - {{ item.nomeImballo }} {{ item.unitaFatturabile }} {{ item.unitaMisura }}}</span>
                </div>

                @if (productEditMode && item.modificabile) {
                  <button
                    matIconButton
                    type="button"
                    class="product-item-edit-button"
                    [attr.aria-label]="t('products.edit')"
                    [disabled]="productEditSaving"
                    (click)="openProductEditor(item)">
                    <i class="fa-regular fa-pencil" aria-hidden="true"></i>
                  </button>
                }
              </div>

              <div class="product-item-data">
                <div class="product-item-row body-medium-emphasized">
                  <span>{{ t('products.quantity') }}</span>
                  <span class="product-item-value">
                    @if (productEditMode && hasProductFieldChanged(item, 'qtaImballi')) {
                      <span class="changed-value-original body-medium">
                        {{ originalItem?.qtaImballi | translocoDecimal: { useGrouping: true, maximumFractionDigits: 0 } }} {{ t('products.piecesUnit') }}
                      </span>
                      <span class="changed-value-arrow body-medium" aria-hidden="true">→</span>
                    }
                    @if (item.qtaImballi != null) {
                      {{ item.qtaImballi | translocoDecimal: {
                        useGrouping: true,
                        maximumFractionDigits: 0
                      } }} {{ t('products.piecesUnit') }}
                    } @else { - }
                  </span>
                </div>

                <div class="product-item-row body-medium-emphasized">
                  <span>{{ t('products.unitPrice') }}</span>
                  <span class="product-item-value">
                    @if (productEditMode && hasProductFieldChanged(item, 'prezzo')) {
                      <span class="changed-value-original body-medium">
                        {{ originalItem?.prezzo | translocoCurrency: 'symbol' : {
                          useGrouping: true,
                          minimumFractionDigits: 4,
                          maximumFractionDigits: 4
                        } : 'EUR' }}@if (originalItem?.unitaMisura) {/{{ originalItem?.unitaMisura }}}
                      </span>
                      <span class="changed-value-arrow body-medium" aria-hidden="true">→</span>
                    }
                    @if (item.prezzo != null) {
                      {{ item.prezzo | translocoCurrency: 'symbol' : {
                        useGrouping: true,
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4
                      } : 'EUR' }}@if (item.unitaMisura) {/{{ item.unitaMisura }}}
                    } @else {
                      -
                    }
                  </span>
                </div>

                <div class="product-item-row body-medium-emphasized">
                  <span>{{ t('products.agentCommission') }}</span>
                  <span class="product-item-value">
                    @if (productEditMode && hasProductFieldChanged(item, 'pProvvigione')) {
                      <span class="changed-value-original body-medium">
                        {{ ((originalItem?.pProvvigione ?? 0) / 100) | translocoPercent: {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        } }}
                      </span>
                      <span class="changed-value-arrow body-medium" aria-hidden="true">→</span>
                    }
                    @if (item.pProvvigione != null) {
                      {{ (item.pProvvigione / 100) | translocoPercent: {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      } }}
                    } @else {
                      -
                    }
                  </span>
                </div>

                <div class="product-item-row body-medium-emphasized">
                  <span>{{ t('products.noProfit') }}</span>
                  <span class="product-item-value">
                    @if (productEditMode && hasProductFieldChanged(item, 'pNoProfit')) {
                      <span class="changed-value-original body-medium">
                        {{ ((originalItem?.pNoProfit ?? 0) / 100) | translocoPercent: {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        } }}
                      </span>
                      <span class="changed-value-arrow body-medium" aria-hidden="true">→</span>
                    }
                    @if (item.pNoProfit != null) {
                      {{ (item.pNoProfit / 100) | translocoPercent: {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      } }}
                    } @else {
                      -
                    }
                  </span>
                </div>

                <div class="product-item-row body-medium-emphasized">
                  <span>{{ t('products.outOfBonus') }}</span>
                  <span class="product-item-value">
                    @if (productEditMode && hasProductFieldChanged(item, 'pFuoriPremio')) {
                      <span class="changed-value-original body-medium">
                        {{ ((originalItem?.pFuoriPremio ?? 0) / 100) | translocoPercent: {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        } }}
                      </span>
                      <span class="changed-value-arrow body-medium" aria-hidden="true">→</span>
                    }
                    @if (item.pFuoriPremio != null) {
                      {{ (item.pFuoriPremio / 100) | translocoPercent: {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      } }}
                    } @else {
                      -
                    }
                  </span>
                </div>
              </div>
            </div>
          }
        </div>

        @if (isDesktop && productEditMode) {
          <div class="task-detail-actions task-detail-actions-desktop product-edit-actions">
            <button
              matButton="outlined"
              type="button"
              class="task-detail-action-button"
              [disabled]="productEditSaving"
              (click)="cancelProductEdit()">
              {{ t('products.editDialog.cancel') }}
            </button>
            <button
              matButton="filled"
              type="button"
              class="task-detail-action-button"
              [disabled]="!hasProductChanges || productEditSaving"
              (click)="confirmProductEdit()">
              {{ t('products.editDialog.confirm') }}
            </button>
          </div>
        } @else if (isDesktop) {
          <ng-container
            *ngTemplateOutlet="taskActionsTemplate; context: { desktop: true }">
          </ng-container>
        }
      </div>
    </mat-tab>
  </mat-tab-group>

  @if (!isDesktop) {
    <ng-container
      *ngTemplateOutlet="taskActionsTemplate; context: { desktop: false }">
    </ng-container>
  }
</ng-container>
